import { Link } from 'react-router-dom'
import Section from '@/components/Section'
import ColorPicker from '@/components/widgets/ColorPicker'
import QuoteCard from '@/components/widgets/QuoteCard'
import RandomColorCard from '@/components/widgets/RandomColorCard'
import AvailabilityDonut from '@/components/widgets/AvailabilityDonut'
import NewsletterForm from '@/components/widgets/NewsletterForm'
import { usePageTitle } from '@/components/usePageTitle'
import { useEffect, useState } from 'react'
import { stats, type Stats } from '@/lib/api'

export default function Home() {
  usePageTitle(undefined, 'Name and register your favorite color.')
  const [s, setS] = useState<Stats | null>(null)
  useEffect(() => {
    stats().then(setS)
  }, [])

  return (
    <>
      {/* 1. Hero */}
      <Section image="/images/cm-bg.webp" tone="light" fullHeight className="text-center">
        <h1 className="mx-auto max-w-5xl pt-20">
          <Link to="/register" className="decoration-[0.04em] underline-offset-[0.12em]">
            Register your favorite color.
          </Link>
        </h1>
      </Section>

      {/* 2. Registration process + picker */}
      <Section>
        <div className="mx-auto grid max-w-[1800px] grid-cols-1 items-start gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <h1>Registration process:</h1>
            <ol className="mt-8 space-y-6 pl-8 md:pl-10">
              <li>
                <h2>1. Pick your color.</h2>
              </li>
              <li>
                <h2>2. Name your color.</h2>
              </li>
              <li>
                <h2>3. Purchase registration.</h2>
              </li>
            </ol>
            <p className="mt-10 max-w-3xl">
              All registrations include a <Link to="/color/example">custom website</Link> with{' '}
              <Link to="/color/example">digital certificate</Link>. ColorMark registrations provide exclusive naming
              rights for your color during the duration of your registration. Lifetime registration option is now
              available.
            </p>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ColorPicker />
          </div>
        </div>
      </Section>

      {/* 3. Quote on paint */}
      <Section image="/images/color-paint-bg.webp" overlay={0.09} tone="light">
        <div className="mx-auto max-w-[1800px]">
          <QuoteCard />
        </div>
      </Section>

      {/* 4. See it / Brand it / Gift it */}
      <Section>
        <div className="mx-auto max-w-[1800px]">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-3 md:gap-10">
            <div>
              <RandomColorCard />
              <h3 className="mt-10">See it.</h3>
              <p className="mt-6">
                The human eye can distinguish over 10 million different chromaticities. The visible spectrum of light
                ranges from 380 to 740 nanometers in wavelength. Find your exact favorite color and register it today.
                If you can see it, then you can own it.
              </p>
            </div>
            <div>
              <RandomColorCard />
              <h3 className="mt-10">Brand it.</h3>
              <p className="mt-6">
                The right color can make you or your business stand out. Register your color and exclusively name it
                for your personal or professional use. Protect and promote your brand with your certified registration
                from the International ColorMark Organization.
              </p>
            </div>
            <div>
              <RandomColorCard />
              <h3 className="mt-10">Gift it.</h3>
              <p className="mt-6">
                Personalized colors make delightful, unique gifts. Registrations include custom website with digital
                certificate and are great for birthdays, weddings, holidays, or any occasion. Customized merchandise
                such as posters, canvas, and framed prints are available.
              </p>
            </div>
          </div>
          <div className="mt-14 text-center">
            <Link to="/register" className="btn btn-gray">
              Register Now
            </Link>
          </div>
        </div>
      </Section>

      {/* 5. Gift of light */}
      <Section image="/images/sky-light-bg.webp" overlay={0.08} className="text-center">
        <h1>Give the gift of light.</h1>
      </Section>

      {/* 6. Millions of colors */}
      <Section>
        <div className="mx-auto grid max-w-[1800px] grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <h2>Millions of colors still available.</h2>
            <ul className="mt-8 list-disc space-y-4 pl-8 text-[1.35em] leading-snug">
              <li>{(s?.total ?? 16_777_216).toLocaleString('en-US')} total colors</li>
              <li>{(s?.premium ?? 12_300).toLocaleString('en-US')} premium colors</li>
              <li>{(s?.reserved ?? 140).toLocaleString('en-US')} reserved colors</li>
              <li>
                <Link to="/register">Register your color now</Link>
              </li>
            </ul>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <AvailabilityDonut stats={s} />
          </div>
        </div>
      </Section>

      {/* 7. Newsletter on smoke */}
      <Section image="/images/color-smoke-bg.webp" overlay={0.11} tone="light">
        <NewsletterForm tone="light" />
      </Section>

      {/* 8. Own the rainbow */}
      <Section className="text-center">
        <h2>
          <Link to="/register">Own the rainbow, with ColorMark.</Link>
        </h2>
      </Section>
    </>
  )
}
